import { createContext, useContext, useState, useCallback, useEffect } from "react";

const STORAGE_KEY = "vts_session_v1";
const AuthContext = createContext(null);

// Read from localStorage with sessionStorage fallback (migrates older clients).
const readStored = () => {
  try {
    let raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      // One-time migration: if a session exists in sessionStorage (older
      // builds), copy it to localStorage so the user stays signed in.
      raw = sessionStorage.getItem(STORAGE_KEY);
      if (raw) localStorage.setItem(STORAGE_KEY, raw);
    }
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const AuthProvider = ({ children }) => {
  const [session, setSession] = useState(() => readStored());

  const login = useCallback((newSession) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newSession));
    // Keep sessionStorage in sync too — some legacy code paths still read it.
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(newSession));
    setSession(newSession);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(STORAGE_KEY);
    setSession(null);
  }, []);

  // Swap which account in session.accounts[] is the active one. Re-uses the
  // existing OAuth access_token (works across all accounts under the same
  // Deriv user) and triggers Dashboard's reconnect-effect via account_id +
  // currency changing.
  const switchAccount = useCallback((newAccountId) => {
    setSession((prev) => {
      if (!prev?.accounts) return prev;
      const next = prev.accounts.find((a) => a.account_id === newAccountId);
      if (!next) return prev;
      const updated = {
        ...prev,
        account_id: next.account_id,
        currency: next.currency || prev.currency,
        token: next.token || prev.token,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      return updated;
    });
  }, []);

  // Keep tabs in sync — if user signs out in one tab, others react too.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== STORAGE_KEY) return;
      setSession(e.newValue ? JSON.parse(e.newValue) : null);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  return (
    <AuthContext.Provider value={{ session, login, logout, switchAccount, isAuthed: !!session?.token }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
